import { For, Show, createMemo, createSignal } from "solid-js";

import { useAiModelCatalog, useAiSettings, useUpdateAiSettings } from "~/lib/hooks";
import type {
  AIModelCatalogEntry,
  EffectiveAISettings,
  WorkspaceAISettingsUpdate,
} from "~/lib/types";

type Props = {
  wsKey: () => string;
};

/**
 * Workspace-tier AI model picker.
 *
 * Reads the effective view (partner ∩ workspace) and the live ScaiGrid
 * catalog. Admin checks/unchecks models from the catalog; items the
 * partner has removed are rendered greyed-out with a "blocked by partner"
 * note so the admin understands *why* something is unavailable.
 *
 * The default-model picker is filtered to the currently-selected models.
 * Saving an empty list explicitly disables AI for this workspace; saving
 * with no checkboxes ticked but `is_unrestricted=true` posts a null
 * allowlist (inherit partner).
 */
export function AISettingsPanel(props: Props) {
  const settings = useAiSettings(props.wsKey);
  const catalog = useAiModelCatalog(props.wsKey, () => settings.data != null);
  const update = useUpdateAiSettings(props.wsKey);

  // Edit state. `null` allowed_model_ids means "inherit"; empty array
  // means "explicitly disabled". The UI translates between checkboxes
  // + a "no restriction" toggle.
  const [unrestricted, setUnrestricted] = createSignal(false);
  const [selectedIds, setSelectedIds] = createSignal<Set<string>>(new Set());
  const [defaultModelId, setDefaultModelId] = createSignal<string>("");
  const [budgetCents, setBudgetCents] = createSignal<string>("");
  const [error, setError] = createSignal<string | null>(null);
  const [hydrated, setHydrated] = createSignal(false);

  // One-shot hydration from server values.
  const initFromServer = () => {
    if (hydrated()) return;
    const row = settings.data?.workspace;
    if (!row && settings.data == null) return;
    if (!row) {
      // No workspace-tier row yet — start with "inherit" defaults.
      setUnrestricted(true);
      setSelectedIds(new Set<string>());
      setDefaultModelId(settings.data?.effective.default_model_id ?? "");
      setBudgetCents("");
    } else {
      const allowed = row.allowed_model_ids;
      setUnrestricted(allowed === null);
      setSelectedIds(new Set(allowed ?? []));
      setDefaultModelId(row.default_model_id ?? "");
      setBudgetCents(
        row.monthly_inference_budget_cents !== null
          ? String(row.monthly_inference_budget_cents)
          : "",
      );
    }
    setHydrated(true);
  };
  // Run on every render; the guard makes it idempotent.
  initFromServer();

  const effective = (): EffectiveAISettings | undefined => settings.data?.effective;
  const partnerBlocked = (modelId: string): boolean => {
    const partner = effective()?.partner_allowed_model_ids;
    if (partner === null || partner === undefined) return false;
    return !partner.includes(modelId);
  };

  // Models shown in the picker: the live catalog from ScaiGrid, plus any
  // already-selected ids the catalog doesn't return (so a deprecated
  // model the admin has saved doesn't silently disappear from the UI).
  const allModels = createMemo<AIModelCatalogEntry[]>(() => {
    const catEntries = catalog.data?.models ?? [];
    const known = new Set(catEntries.map((m) => m.id));
    const orphans = Array.from(selectedIds())
      .filter((id) => !known.has(id))
      .map((id) => ({
        id,
        label: `${id} (no longer in catalog)`,
        vendor: null,
        family: null,
        context_window: null,
        input_price_per_1k_cents: null,
        output_price_per_1k_cents: null,
      }));
    return [...catEntries, ...orphans];
  });

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    setError(null);
    const payload: WorkspaceAISettingsUpdate = {
      allowed_model_ids: unrestricted() ? null : Array.from(selectedIds()),
      default_model_id: defaultModelId().trim() || null,
      monthly_inference_budget_cents: budgetCents().trim() === "" ? null : Number(budgetCents()),
    };
    try {
      await update.mutateAsync(payload);
      setHydrated(false); // re-hydrate from the saved response
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body">
        <div>
          <h3 class="card-title text-base">AI models</h3>
          <p class="text-xs opacity-60 mt-0.5">
            Pre-select the models agents in this workspace may use. The pool is the intersection of
            what your partner allows and what you pick here.
          </p>
        </div>

        <Show when={settings.isLoading}>
          <p class="text-sm opacity-60 mt-2">Loading…</p>
        </Show>

        <Show when={catalog.data && !catalog.data.scaigrid_configured}>
          <div role="alert" class="alert alert-warning text-sm mt-2">
            ScaiGrid isn't configured on this deployment. Set
            <code class="ml-1">SCAICRM_SCAIGRID_API_KEY</code> to enable AI.
          </div>
        </Show>

        <Show when={effective() && effective()!.partner_allowed_model_ids !== null}>
          <p class="text-xs opacity-70 mt-2">
            Partner allows <strong>{effective()!.partner_allowed_model_ids?.length ?? 0}</strong>{" "}
            models. Items outside that list are greyed out below.
          </p>
        </Show>

        <Show when={effective()?.ai_enabled === false}>
          <div role="alert" class="alert alert-error text-sm mt-2">
            AI is currently disabled (empty allowlist at this or a parent tier).
          </div>
        </Show>

        <div class="form-control mt-3">
          <label class="label cursor-pointer justify-start gap-2">
            <input
              type="checkbox"
              class="checkbox checkbox-sm"
              checked={unrestricted()}
              onChange={(e) => setUnrestricted(e.currentTarget.checked)}
            />
            <span class="label-text">Inherit partner policy (no workspace-level restriction)</span>
          </label>
        </div>

        <Show when={!unrestricted()}>
          <div class="mt-2 max-h-72 overflow-y-auto border border-base-300 rounded-md">
            <Show
              when={allModels().length > 0}
              fallback={
                <p class="p-3 text-sm opacity-60">
                  No models in catalog. Check ScaiGrid configuration.
                </p>
              }
            >
              <For each={allModels()}>
                {(m) => {
                  const blocked = partnerBlocked(m.id);
                  return (
                    <label
                      class={`flex items-start gap-2 p-2 border-b border-base-200 last:border-b-0 ${
                        blocked ? "opacity-40" : "cursor-pointer hover:bg-base-200"
                      }`}
                    >
                      <input
                        type="checkbox"
                        class="checkbox checkbox-sm mt-1"
                        checked={selectedIds().has(m.id)}
                        disabled={blocked}
                        onChange={() => toggle(m.id)}
                      />
                      <div class="flex-1 text-sm">
                        <div class="font-medium">{m.label}</div>
                        <div class="text-xs opacity-60">
                          <code>{m.id}</code>
                          <Show when={m.vendor}>
                            <span class="ml-2">· {m.vendor}</span>
                          </Show>
                          <Show when={m.context_window}>
                            <span class="ml-2">· {m.context_window!.toLocaleString()} ctx</span>
                          </Show>
                          <Show when={blocked}>
                            <span class="ml-2 text-warning">· blocked by partner</span>
                          </Show>
                        </div>
                      </div>
                    </label>
                  );
                }}
              </For>
            </Show>
          </div>
        </Show>

        <div class="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label class="label" for="ai-default-model">
              <span class="label-text">Default model</span>
            </label>
            <select
              id="ai-default-model"
              class="select select-bordered select-sm w-full"
              value={defaultModelId()}
              onChange={(e) => setDefaultModelId(e.currentTarget.value)}
            >
              <option value="">— inherit / none —</option>
              <For
                each={
                  unrestricted()
                    ? allModels().filter((m) => !partnerBlocked(m.id))
                    : allModels().filter((m) => selectedIds().has(m.id) && !partnerBlocked(m.id))
                }
              >
                {(m) => <option value={m.id}>{m.label}</option>}
              </For>
            </select>
          </div>
          <div>
            <label class="label" for="ai-budget">
              <span class="label-text">Monthly inference budget (cents)</span>
            </label>
            <input
              id="ai-budget"
              type="number"
              min="0"
              class="input input-bordered input-sm w-full"
              placeholder="Unlimited"
              value={budgetCents()}
              onInput={(e) => setBudgetCents(e.currentTarget.value)}
            />
            <p class="text-xs opacity-60 mt-1">
              Reserved — enforcement lands when the budget layer ships.
            </p>
          </div>
        </div>

        <Show when={error()}>
          <div role="alert" class="alert alert-error text-sm mt-2">
            {error()}
          </div>
        </Show>

        <div class="flex justify-end mt-3">
          <button
            type="button"
            class="btn btn-primary btn-sm"
            onClick={handleSave}
            disabled={update.isPending}
          >
            {update.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
